<template>
  <template v-if="isSimplifiedUploadEnabled">
    <StudioButton trackingId="new-content-upload-button" :variant="buttonVariant" @click="openNewContentModal">
      <template #icon> <TablerPlus /> </template>
      Upload
    </StudioButton>
  </template>
  <template v-else-if="uploadFlow === 'legacy'">
    <UploadVideo @success="onUploadSuccess" @init="onUploadInit" @failure="onUploadFailure" @close="onUploadClose">
      <StudioButton trackingId="upload-video-button" :variant="buttonVariant">
        <template #icon> <TablerPlus /> </template>
        Upload Video
      </StudioButton>
    </UploadVideo>
  </template>
  <template v-else-if="uploadFlow === 'content-lab-import'">
    <StudioButton
      trackingId="content-lab-import-video-button"
      :variant="buttonVariant"
      @click="navigateToContentLabImport"
    >
      <template #icon> <TablerPlus /> </template>
      Import Video
    </StudioButton>
  </template>
  <template v-else-if="uploadFlow === 'content-lab-federated' && !microfrontendFailed">
    <div id="content-lab-federated-import-video-button" class="content-lab-root" />
  </template>
  <!--
    isSimplifiedUploadEnabled requires both the Use_CL_Simplified_Upload FF and a non-legacy
    uploadFlow, so branch 1 only wins for content-lab-import / content-lab-federated orgs that
    have the FF on. With the FF off, those orgs fall through to their original branches below
    (Import Video button / federated legacy import dialog); legacy orgs are unaffected either way.
  -->
</template>

<script setup lang="ts">
import { retrieve_4 as retrieveVideoLibraryAsset } from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

import UploadVideo from '../UploadVideo.vue';
import { transformRawVideoAsset, VideoAsset } from '../videoLibraryService';

import { openNewContentModal } from '@/studio/contentLab/newContentModal';
import renderReactComponent from '@/studio/contentLab/renderReactComponent';
import TablerPlus from '@/studioComponents/Icons/tabler/TablerPlus.vue';
import StudioButton, { StudioButtonVariant } from '@/studioComponents/StudioButton.vue';

const props = defineProps<{
  buttonVariant?: StudioButtonVariant;
  uploadFlow: 'legacy' | 'content-lab-import' | 'content-lab-federated';
}>();

const emit = defineEmits<{
  (e: 'upload:success', newVideoAsset: VideoAsset | undefined): void;
}>();

const isUploadingNewFile = ref(false);
const microfrontendFailed = ref(false);
let retryCount = 0;
const MAX_RETRIES = 3;
let unmount: (() => void) | null = null;
let retryTimeout: NodeJS.Timeout | null = null;

const store = useStore();

// The simplified "New upload" modal only rides the content-lab federated pipeline, so it must
// only appear where that pipeline already runs (content-lab-import and content-lab-federated),
// and only once the org has the FF on. Everyone else keeps the original per-uploadFlow UI below.
const isSimplifiedUploadEnabled = computed(
  () =>
    Boolean(store?.getters['org/getCurrentOrg']?.Use_CL_Simplified_Upload) &&
    (props.uploadFlow === 'content-lab-federated' || props.uploadFlow === 'content-lab-import')
);

const loadMicrofrontend = () => {
  if (!document.getElementById('content-lab-federated-import-video-button')) {
    return;
  }

  import('content_studio/renderVideoImportDialog' as any)
    .then(({ default: renderVideoImportDialog }) => {
      unmount = renderReactComponent(
        renderVideoImportDialog,
        'content-lab-federated-import-video-button',
        {},
        {
          onSuccess: async (videoAssetId: string) => {
            store.dispatch('studioToast/triggerToast', {
              msg: 'Video imported successfully',
              mode: 'success'
            });

            if (!videoAssetId) {
              emit('upload:success', undefined);
              return;
            }

            try {
              const videoAsset = await retrieveVideoLibraryAsset({ id: videoAssetId });
              const transformedVideoAsset = transformRawVideoAsset(videoAsset);
              emit('upload:success', transformedVideoAsset);
            } catch (error) {
              emit('upload:success', undefined);
            }
          }
        }
      );
      // Reset retry count and failure flag if successful
      retryCount = 0;
      microfrontendFailed.value = false;
    })
    .catch(err => {
      logger.error(
        `[VideoLibraryUploadButton] Failed importing content_studio/renderVideoImportDialog (attempt ${retryCount + 1}/${MAX_RETRIES})`,
        err
      );
      retryCount++;

      if (retryCount < MAX_RETRIES) {
        // Retry with exponential backoff (1s, 2s, 4s, etc.)
        const delay = Math.pow(2, retryCount - 1) * 1000;
        logger.info(`[VideoLibraryUploadButton] Retrying in ${delay}ms...`);
        retryTimeout = setTimeout(loadMicrofrontend, delay);
      } else {
        logger.error(`[VideoLibraryUploadButton] Failed to load microfrontend after ${MAX_RETRIES} attempts`);
        microfrontendFailed.value = true;
        store.dispatch('studioToast/triggerToast', {
          msg: 'Something went wrong while initializing video import. Please refresh the page and try again.',
          mode: 'error'
        });
      }
    });
};

onMounted(() => {
  if (!isSimplifiedUploadEnabled.value && props.uploadFlow === 'content-lab-federated') {
    loadMicrofrontend();
  }
});

onUnmounted(() => {
  if (unmount) {
    unmount();
  }

  if (retryTimeout) {
    clearTimeout(retryTimeout);
  }
});

const onUploadSuccess = (newVideoAsset: VideoAsset) => {
  emit('upload:success', newVideoAsset);
};

const onUploadInit = () => {
  isUploadingNewFile.value = true;
};

const onUploadFailure = () => {
  isUploadingNewFile.value = false;
};

const onUploadClose = () => {
  isUploadingNewFile.value = false;
};

const navigateToContentLabImport = () => {
  store.dispatch('contentStudio/openContentLabImport');
};

defineExpose({
  isUploadingNewFile
});
</script>
